"use client";

import * as Select from "@radix-ui/react-select";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { usePathname, useRouter } from "next/navigation";
import i18nConfig from "@@/i18nConfig";
import { montserratMedium } from "@@/utils/fonts";
import { JSX, useMemo } from "react";
import styles from "@@/styles/components/header/LanguageSwitch.module.scss";
import flagFR from "@@/public/img/shared/fr.png";
import flagEN from "@@/public/img/shared/en.png";
import flagNL from "@@/public/img/shared/nl.png";
import flagBE from "@@/public/img/shared/be.png";
import { useHeaderCategoryContext } from "@@/hooks/contexts/headerCategroyContext";


function ChevronDown(props: JSX.IntrinsicElements["svg"]) {
	return (
		<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden {...props}>
			<path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}
function Check(props: JSX.IntrinsicElements["svg"]) {
	return (
		<svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden {...props}>
			<path d="M4 10l3 3 9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

const FLAG_MAP: Record<string, any> = {
	fr: flagFR,
	en: flagEN,
	nl: flagNL,
	be: flagBE,
};

interface ILanguageSwitchProps {
	scrollTop?: boolean;
	backgroundIsWhite?: boolean;
	className?: string;
}

export default function LanguageSwitchRadix({
												scrollTop,
												backgroundIsWhite,
												className,
											}: ILanguageSwitchProps): JSX.Element {
	const { setHoveredCategory } = useHeaderCategoryContext();
	const { i18n } = useTranslation();
	const currentLocale = i18n.language; // "fr" | "en" | "nl" | "be"
	const router = useRouter();
	const pathname = usePathname();

	const currentFlag = useMemo(
		() => FLAG_MAP[currentLocale] ?? FLAG_MAP["fr"],
		[currentLocale]
	);

	function languageHandler(newLocale: string) {
		if (!newLocale || newLocale === currentLocale) return;

		// Cookie pour next-i18n-router
		const days = 30;
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		document.cookie = `NEXT_LOCALE=${newLocale};expires=${date.toUTCString()};path=/`;

		// Redirection
		if (currentLocale === i18nConfig.defaultLocale) {
			router.push("/" + newLocale + pathname);
		} else {
			router.push(pathname.replace(`/${currentLocale}`, `/${newLocale}`));
		}
		router.refresh();
	}

	return (
		<div className={`d-flex ${styles.main} ${(!scrollTop || backgroundIsWhite) ? styles.dark : styles.light} align-items-center align-items-end-mobile h-100 h-fit-mobile pb-5-mobile flex-1-mobile`}>
			<Select.Root value={currentLocale} onValueChange={languageHandler} onOpenChange={(open) => {if (open) setHoveredCategory(undefined);}}>
				<Select.Trigger
					className={[
						"d-flex align-items-center gap-1 h-9 p-1 rounded-md border",
						"bg-white/80 backdrop-blur text-sm outline-none",
						"hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
						(!scrollTop || backgroundIsWhite) ? "border-gray-300 text-gray-900" : "border-white/50 text-white",
						className ?? "",
						montserratMedium.className,
					].join(" ")}
					aria-label="Choisir la langue"
					onPointerDown={() => setHoveredCategory(undefined)}
				>
					<Image
						src={currentFlag}
						alt={`Drapeau ${currentLocale.toUpperCase()}`}
						width={20}
						height={20}
						className="rounded-[2px] object-cover"
					/>
					<Select.Value aria-label={currentLocale}>
						{currentLocale.toUpperCase()}
					</Select.Value>
					<Select.Icon className="ml-1 opacity-75">
						<ChevronDown />
					</Select.Icon>
				</Select.Trigger>

				<Select.Portal>
					<Select.Content
						className="z-99 overflow-hidden rounded-md border bg-light shadow-lg d-flex flex-column color-blue rounded-1"
						position="popper"
						sideOffset={6}
					>
						<Select.Viewport className="p-1">
							{i18nConfig.locales.map((locale) => {
								const flag = FLAG_MAP[locale] ?? FLAG_MAP["fr"];
								return (
									<Select.Item
										key={locale}
										value={locale}
										className={[
											"relative d-flex cursor-pointer select-none items-center gap-1 rounded px-2 p-1 py-1 text-sm align-items-center justify-end",
											"outline-none hover:bg-gray-100 focus:bg-gray-100 text-gray-900 hover-bg-light",
										].join(" ")}
									>
										<Select.ItemIndicator className="absolute left-1">
											<Check />
										</Select.ItemIndicator>
										<Image
											src={flag}
											alt={`Drapeau ${locale.toUpperCase()}`}
											width={20}
											height={20}
											className="rounded-[2px] object-cover"
										/>
										<Select.ItemText>{locale.toUpperCase()}</Select.ItemText>
									</Select.Item>
								);
							})}
						</Select.Viewport>
					</Select.Content>
				</Select.Portal>
			</Select.Root>
		</div>
	);
}
